/** biome-ignore-all lint/style/noNestedTernary: no nested ternaries */
/** biome-ignore-all lint/correctness/useJsxKeyInIterable: jsx key in iterable */
import cx from 'classnames';
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {EditButton} from '~/common/components/EditButton/EditButton';
import {useConnectEditor} from '~/ViewInvitation/hooks/useConnectEditor';
import {refreshFundraiser} from '~/ViewInvitation/reducers/apiActions';
import {DONATION_MADE} from '~/ViewInvitation/reducers/constants';
import {selectDonation} from '~/ViewInvitation/selectors/donation';
import {isPreview} from '~/ViewInvitation/utils';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {DonationPanel} from './DonationPanel';

import './DonationsModule.sass';

const pledgeLogo = (
  <img
    src="https://g0.evitecdn.com/apps/logos/third-party/powered-by-pledge.png"
    alt="pledge logo"
    width="62"
  />
);

const DonationSegment = ({
  donation,
  readonly,
  handleActivate,
  isDesktop,
  preview = isPreview(),
}) => {
  const {found, donation_url: donationUrl, id} = donation;
  const containerRef = useRef(null);
  const editBtnRef = useRef(null);
  const name = useSelector((state) => state.rsvp?.name ?? state.user?.name ?? '');
  const firstName = name?.split(' ')[0];
  const lastName = name?.split(' ')[1];
  const guestEmail = useSelector((state) => state.rsvp?.guestEmail ?? '');

  const hasDonation = !!donationUrl || donationUrl !== undefined; // along with initial state, prevents flash

  // get the key and host from the url that pledge sends back to us on create
  const tempUrl = document.createElement('a');
  tempUrl.href = donationUrl;
  const pledgelingHost = `${tempUrl.protocol}//${tempUrl.hostname}/assets/widget.js`;
  const partnerKey = tempUrl.pathname.split('/')[3];

  const dispatch = useDispatch();

  // biome-ignore lint/correctness/useExhaustiveDependencies: This didn't have the other deps before, I assume there is a good reason
  const onMessage = useCallback(
    (e) => {
      if (!e.origin.includes('pledge.to')) return;
      const {data} = e;
      let objMessage;
      try {
        objMessage = JSON.parse(data);
      } catch {
        return; // we don't care about this
      }

      if (objMessage.action === 'donate') {
        handleActivate(objMessage.data.itemId, partnerKey);
      } else {
        let value = parseFloat(objMessage.data?.amount);
        switch (objMessage.message) {
          case 'DonateCompleted':
            value *= 100; // store money in cents
            dispatch({
              type: DONATION_MADE,
              results: {
                donationUrl,
                donationAddedCents: value,
              },
            });
            dispatch(refreshFundraiser()); // tell backend to refresh cache
            break;
          case 'setWidgetHeight':
            break;
          case 'intersectionLoaded':
            break;
          default:
            evite.log('Pledge unhandled message: ', objMessage.message);
        }
      }
    },
    [dispatch, donationUrl]
  );

  useEffect(() => {
    if (donationUrl) {
      const script = document.createElement('script');
      script.src = pledgelingHost;
      script.id = 'plg-widget';
      script.async = true;
      document.body.appendChild(script);

      window.addEventListener('message', onMessage, false);
      return () => {
        window.removeEventListener('message', onMessage);
      }; // will happen on unmount
    }
  }, [donationUrl, pledgelingHost, onMessage]);

  const donationType = useMemo(() => {
    if (id?.includes('Organization')) return 'charity';
    if (id?.includes('Crowdfund')) return 'crowdfund';
    return 'donation';
  }, [id]);

  const editable = preview && !readonly && isDesktop;
  useConnectEditor(containerRef, editBtnRef, editable && donationUrl);

  if (found && donationUrl) {
    return (
      <div className={cx('donations-module__donation-segment', {editable})} ref={containerRef}>
        {editable && <div className="module__edit-hover" />}
        <EditButton data-qa-id={`edit-${donationType}`} ref={editBtnRef} hide={!editable} />
        <div id="external-donation-id">
          {hasDonation ? (
            <div
              className="plg-donate"
              data-partner-key={partnerKey}
              data-item-id={id || window.event_id || window.evite_event.id}
              data-x-user-id={window.user_id || ''}
              data-first-name={firstName || ''}
              data-last-name={lastName || ''}
              data-email={guestEmail || ''}
              data-style-hide-social="true"
              data-template="branding-2025-10"
            />
          ) : // <Typography variant="paragraph3">
          //   {/* TODO: This text will need to be updated when multiple donation streams become supported */}
          //   Your host has removed donations for this event.
          // </Typography>
          null}
        </div>
      </div>
    );
  }
  return (
    <Typography variant="paragraph3">Error loading donation, please try again later.</Typography>
  );
};

export const DonationsModule = memo((props) => {
  const {loading: isLoading, donations: propDonation, readonly, preview = isPreview()} = props;
  const donation = useSelector(selectDonation);
  const donations = propDonation || donation.donations;
  const loading = isLoading || donation.loading;

  const moduleContainerRef = useRef(null);
  const editModuleRef = useRef(null);
  const isDesktop = useIsLargishAndUp();
  const [sortedDonations, setSortedDonations] = useState(donations);
  const [activePledgeId, setActivePledgeId] = useState(null);
  const [partnerKey, setPartnerKey] = useState(null);
  const sidePanelOpen = !!activePledgeId;

  useEffect(() => {
    if (donations?.length > 0) {
      // since Charity goes first, lets reverse sort by fundraiser type because those values
      // can be Organization and Crowdfund, and Organization should be first
      setSortedDonations(
        donations.sort((a, b) => b.fundraiser_type?.localeCompare(a.fundraiser_type))
      );
    }
  }, [donations]);

  const onActivate = useCallback((id, partnerKey) => {
    setActivePledgeId(id);
    setPartnerKey(partnerKey);
  }, []);

  const onClose = useCallback(() => {
    setActivePledgeId(null);
  }, []);

  const editable = preview && !readonly && !isDesktop;
  useConnectEditor(moduleContainerRef, editModuleRef, editable && donations.length > 0);

  return (
    <>
      <EditButton data-qa-id="edit-donations-module" ref={editModuleRef} hide={!editable} />
      <div className="donations-module__donation-segments">
        <div className="donations-module__header">
          <Typography variant="label2" data-qa-id="donations-module-title" color="pebble">
            Give
          </Typography>
          <Typography variant="header6">Donate to their chosen cause</Typography>
          <Typography className="donations-module__pledge-logo" variant="paragraph3">
            Powered by {pledgeLogo}
          </Typography>
        </div>
        {loading ? (
          <div className="view-invitation__donation-loading">
            <span className="evt-spinner inline dark" />
            <Typography variant="paragraph2">Loading...</Typography>
          </div>
        ) : (
          sortedDonations.map((d) =>
            !d.donation_url ? (
              <Typography variant="paragraph3" key={d.id}>
                Error loading{' '}
                {d.id?.includes('Organization')
                  ? 'charity'
                  : d.id?.includes('Crowdfund')
                    ? 'crowdfund'
                    : 'donation'}
                , please try again later.
              </Typography>
            ) : !d.found ? (
              <Typography variant="paragraph3">
                Your host has removed donations for this event.
              </Typography>
            ) : (
              <DonationSegment
                {...props}
                readonly={readonly || !isDesktop}
                donation={d}
                key={d.id}
                isDesktop={isDesktop}
                handleActivate={onActivate}
              />
            )
          )
        )}

        <DonationPanel
          id={activePledgeId}
          partnerKey={partnerKey}
          open={sidePanelOpen}
          onClose={onClose}
        />
      </div>
    </>
  );
});

DonationsModule.displayName = 'DonationsModule';
